import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isIOS } from 'react-device-detect';

import { getSecondsFromTimelineTime, getTimelineTimeFromTimeInput } from '../../../util/util';
import { PLAYER_TRANSITIONS_OUT, WIDGET_TYPE_IFRAME } from '../../../util/constants';
import { usePlayer } from '../../../contexts/PlayerContext';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { useShopping } from '../../../contexts/ShoppingContext';

import SVGWrapper from './SVGWrapper';
import ScalableElement from './ScalableElement';
import ProductElement from './ProductElement';
import WidgetElement, { GroupExit } from './WidgetElement';
import { INTERACT_ELEMENT_ACTIONS, WIDGET_ACTIONS } from './util';

import 'animate.css';

import './InteractOverlay.scss';
import './WidgetAnimation.scss';
import _ from 'underscore';

const SCENE_END_GROUP_EXIT: GroupExit = {};

interface InteractOverlayProps {
	settings: any;
	isMobile?: boolean;
	isMaintainProportions?: boolean;
	onMouseLeave?: () => void;
	onMouseMove?: () => void;
	handlePlayerHover?: () => void;
	onTouchMove?: () => void;
}

const InteractOverlay: React.FC<InteractOverlayProps> = ({
	settings,
	isMobile,
	isMaintainProportions,
	onMouseLeave,
	onMouseMove,
	handlePlayerHover,
	onTouchMove
}) => {
	// Context hooks
	const {
		currentPosition,
		currentPositionMediaId,
		showBackgroundElements,
		currentInteractiveElement,
		duration,
		analyticsSent,
		currentSceneWidgets,
		setAnalyticsSent,
		setCurrentInteractiveElement,
		setShowChatWindow,
		setShowShoppingCart,
		setPlayerCurrentPosition,
	} = usePlayer();

	const {
		mediaName,
		currentMediaId: mediaId,
		playlistId,
		accountId,
		isMediaLive,
		mediaType,
		sceneConfig,
		scenario,
		mediasData,
		showCountdownModule,
		configParams,
		isTransitionLayerEnable,
		isSceneEndExiting,
		setSceneConfig,
		updateSceneHistory,
		setTransitionLayer,
		setTransitionOut,
		setShouldChangeCurrentMedia,
		setChangeSceneType,
	} = useConfiguration();

	const { cartArray, setCartArray, setCurrentPrebasketProduct, setPreBasketUpdated } = useShopping();
	const [currentProduct, setCurrentProduct] = useState<any>(null);
	const [exitingElements, setExitingElements] = useState<any[]>([]);
	// Set when a clicked widget's action leaves the scene (Go to scene, Go to URL in the same
	// window): the other widgets play their exit with the clicked widget's exit timing.
	const [groupExit, setGroupExit] = useState<GroupExit | null>(null);
	// The scene's video ended and it moves to another scene: every widget exits with its own
	// duration, no delay (Player waits for the longest one before the transition).
	const activeGroupExit = groupExit ?? (isSceneEndExiting ? SCENE_END_GROUP_EXIT : null);
	const prevFocusElem = useRef<HTMLElement | null>(null);

	// Widgets that just fell out of [start, end) but have an exitAnimation configured are kept
	// around in `exitingElements` until their animation finishes, so WidgetElement gets a chance
	// to render the exit animation instead of being unmounted the instant the range ends.
	// `exitingElements` is derived from the *same* setCurrentProduct call (via the functional
	// updater) rather than a separate effect reacting to `currentProduct` - otherwise there is a
	// render in between where the widget is in neither list and WidgetElement unmounts, resetting
	// its internal isExiting tracking before the exit animation ever gets a chance to start.
	const updateCurrentProduct = useCallback((next: any) => {
		setCurrentProduct((prevProduct: any) => {
			if (mediaType === 'scenario') {
				const prevElements = Array.isArray(prevProduct) ? prevProduct : [];
				const nextIds = new Set((Array.isArray(next) ? next : []).map((el: any) => el.id));

				const droppedWithExitAnimation = prevElements.filter(
					(el: any) => !nextIds.has(el.id) && Boolean(el?.data?.style?.transformSettings?.exitAnimation)
				);

				setExitingElements((prev) => {
					const map = new Map<string, any>();
					prev.forEach((el: any) => {
						if (!nextIds.has(el.id)) {
							map.set(el.id, el);
						}
					});
					droppedWithExitAnimation.forEach((el: any) => map.set(el.id, el));
					return Array.from(map.values());
				});
			}
			return next;
		});
	}, [mediaType]);

	const handleWidgetExitAnimationComplete = useCallback((id: string) => {
		setExitingElements((prev) => prev.filter((el: any) => el.id !== id));
	}, []);

	// Whenever the scene changes (goToScene click, end-of-scene auto-advance, restart, back,
	// home, etc.), reset the playhead to 0 and drop every widget carried over from the previous
	// scene. Each scene starts its own media timeline at 0, so without this the incoming scene's
	// widgets get evaluated against the previous scene's (stale) position - any widget whose
	// [start, end) spans that value flashes on screen, then vanishes when the new media reports
	// 0, then animates in again. Enforcing it here - in one place - covers every scene-change
	// path so a widget only appears once the new media's position actually enters its range.
	const prevSceneRef = useRef<string | null | undefined>(sceneConfig?.currentScene);
	useEffect(() => {
		if (mediaType !== 'scenario') {
			return;
		}
		if (prevSceneRef.current === sceneConfig?.currentScene) {
			return;
		}
		prevSceneRef.current = sceneConfig?.currentScene;
		setPlayerCurrentPosition(0);
		setCurrentProduct([]);
		setExitingElements([]);
	}, [sceneConfig?.currentScene, mediaType, setPlayerCurrentPosition]);

	// A scene change is announced - transition layer up, playhead reset to 0 - before
	// `currentScene` actually flips: 200ms earlier on end-of-scene auto-advance, two frames
	// earlier on a goToScene click. The reset is what keeps the incoming scene's widgets from
	// being evaluated against the outgoing scene's stale position (see the goToScene click
	// below and Player.onPlayerPlayComplete), but the outgoing scene's widgets are still
	// mounted during that window and their visibility is driven purely by the playhead. A
	// widget whose window starts at 0 - a persistent back/home button, say - therefore sees the
	// playhead land back inside its [start, end) range: it cancels the exit animation it just
	// started when the scene ended and plays its entry animation again, only to be unmounted
	// when the scene finally switches. Dropping the outgoing scene's widgets as soon as the
	// change is announced removes that window.
	// useLayoutEffect (not useEffect): the playhead reset and the transition flag arrive in the
	// same render, and WidgetElement re-applies the entry animation in its own layout effect -
	// so the widgets have to be gone before that render is painted, not after.
	// The scene being left while the transition layer is up. Until currentScene flips, the
	// position effect below must not pick that scene's widgets again: the outgoing player keeps
	// reporting its position (and at the end of the scene it stays at the end), so they would be
	// re-added, replay their entry and flash back after their exit until the switch.
	const leavingSceneRef = useRef<string | null | undefined>(undefined);
	useLayoutEffect(() => {
		if (mediaType !== 'scenario' || !isTransitionLayerEnable) {
			leavingSceneRef.current = undefined;
			return;
		}
		leavingSceneRef.current = sceneConfig?.currentScene;
		setCurrentProduct([]);
		setExitingElements([]);
	}, [isTransitionLayerEnable, mediaType]);

	const currentPlayerId = `player-${playlistId || mediaId}-${configParams.containerKey}`;

	const isScenarioInteractElementsEqual = useCallback((interact1: any, interact2: any) => {
		if (!interact1 && !interact2) {
			return true;
		}

		let sortedInteract1 = Array.from(interact1 ?? []).sort((a: any, b: any) => (a.id > b.id ? 1 : -1));
		let sortedInteract2 = Array.from(interact2 ?? []).sort((a: any, b: any) => (a.id > b.id ? 1 : -1));

		sortedInteract1 = sortedInteract1.map((i: any) => i.id).filter(Boolean);
		sortedInteract2 = sortedInteract2.map((i: any) => i.id).filter(Boolean);

		return sortedInteract1.join(',') === sortedInteract2.join(',');
	}, []);

	const getLiveMediaCurrentProduct = useCallback(() => {
		if (!isMediaLive) {
			return;
		}

		const elements = mediaType === 'scenario' ? currentSceneWidgets : currentInteractiveElement;
		const currentInteractiveElementArray = !elements ? [] : elements.map ? elements : [elements];

		const newProduct =
			mediaType === 'scenario'
				? currentInteractiveElementArray.filter(
					(item: any) =>
						(item.start !== undefined || item.start !== null) &&
						(typeof currentPosition === 'number' ? currentPosition : 0) >= item.start &&
						((typeof currentPosition === 'number' ? currentPosition : 0) < item.end || !item.end)
				)
				: currentInteractiveElementArray.find(
					(item: any) =>
						(item.start !== undefined || item.start !== null) &&
						(typeof currentPosition === 'number' ? currentPosition : 0) >= item.start &&
						((typeof currentPosition === 'number' ? currentPosition : 0) < item.end || !item.end)
				);

		updateCurrentProduct(newProduct);
	}, [isMediaLive, mediaType, currentSceneWidgets, currentInteractiveElement, currentPosition, updateCurrentProduct]);

	const isElementOverlapping = (el1: HTMLElement, el2: HTMLElement | null) => {
		if (!el2) {return false;}
		const domRect1 = el1.getBoundingClientRect();
		const domRect2 = el2.getBoundingClientRect();

		return !(
			domRect1.top > domRect2.bottom ||
			domRect1.right < domRect2.left ||
			domRect1.bottom < domRect2.top ||
			domRect1.left > domRect2.right
		);
	};

	const handleClickOnProduct = useCallback(
		(event: React.MouseEvent, targetElement: any) => {
			const pos = typeof currentPosition === 'number' ? currentPosition : 0;
			var playerElement = document.getElementById(currentPlayerId);
			event.preventDefault();

			var message;
			var priceCurrency = targetElement.price.value.slice(-3);

			if (targetElement.variantCombinations && targetElement.variantCombinations.value && targetElement.variantCombinations.value.length > 0) {
				var variationComboArray = targetElement.variantCombinations.value.map((elem: any) => ({
					comboName: elem.comboName,
					comboId: elem.comboId,
					comboImg: elem.comboImg === '' ? targetElement.imageUrl.value : elem.comboImg,
					comboPrice: elem.comboPrice + ' ' + priceCurrency,
				}));
				message =
					targetElement.prodName.value +
					'=)' +
					targetElement.prodId.value +
					'=)' +
					targetElement.prodPage.value +
					'=)' +
					targetElement.price.value +
					'=)' +
					targetElement.prodDescription.value +
					(targetElement.hideVolumeCounter === undefined ? '' : '(=' + targetElement.hideVolumeCounter.value) +
					'=)' +
					targetElement.imageUrl.value +
					'=)' +
					(targetElement.variantName1.value === undefined ? '' : targetElement.variantName1.value) +
					'=)' +
					(targetElement.variantName2.value === undefined ? '' : targetElement.variantName2.value) +
					'=)' +
					(targetElement.variantValues1.value === undefined ? '' : targetElement.variantValues1.value.join()) +
					'=)' +
					(targetElement.variantValues2.value === undefined ? '' : targetElement.variantValues2.value.join()) +
					'=)' +
					JSON.stringify(variationComboArray);
				playerElement?.querySelectorAll("[data-internal-gobrain-box-configuration-modulename='Controls']")[0]?.parentElement &&
					((playerElement.querySelectorAll("[data-internal-gobrain-box-configuration-modulename='Controls']")[0].parentElement as HTMLElement).style.zIndex =
						'250');
			} else {
				message =
					targetElement.prodName.value +
					'=)' +
					targetElement.prodId.value +
					'=)' +
					targetElement.prodPage.value +
					'=)' +
					targetElement.price.value +
					'=)' +
					targetElement.prodDescription.value +
					(targetElement.hideVolumeCounter === undefined ? '' : '(=' + targetElement.hideVolumeCounter.value) +
					'=)' +
					targetElement.imageUrl.value;
			}

			window.parent.postMessage(
				{
					origin: 'Qbrick.Interact.Widget.Element',
					identifier: 'qbrick-player-analytics-message',
					command: {
						type: 'AnalyticsEvent',
						action: 'click',
						data: 'clickedProduct',
						mediaName: mediaName,
						mediaId: mediaId,
						accountId: accountId,
						duration: duration,
						isMediaLive: isMediaLive,
						currentPosition: isMediaLive ? Date.now() : pos,
						productId: targetElement.prodId.value,
						message: message,
					},
				},
				'*'
			);

			var obj = {
				productId: targetElement.prodId.value,
				productPage: targetElement.prodPage.value,
				name: targetElement.prodName.value,
				price: targetElement.price.value,
				quantity:
					targetElement.hideVolumeCounter === undefined || (targetElement.hideVolumeCounter !== undefined && targetElement.hideVolumeCounter.value === false)
						? '1'
						: '0',
				description: targetElement.prodDescription.value + (targetElement.hideVolumeCounter === undefined ? '' : '(=' + targetElement.hideVolumeCounter.value),
				image: targetElement.imageUrl.value,
			};

			if (targetElement.variantCombinations && targetElement.variantCombinations.value && targetElement.variantCombinations.value.length > 0) {
				if (settings.engagementFeatures.showShoppingCart) {
					setShowShoppingCart(true);
					setCurrentPrebasketProduct(targetElement);
					setPreBasketUpdated(true);
					setShowChatWindow(false);
				}
			} else {
				if (!cartArray.some((el: any) => el.productId === obj.productId)) {
					var tempArray = cartArray;
					tempArray.push(obj);
					setCartArray(tempArray);
					setShowChatWindow(false);
				}
			}
		},
		[
			currentPosition,
			currentPlayerId,
			mediaName,
			mediaId,
			accountId,
			duration,
			isMediaLive,
			settings,
			cartArray,
			setShowShoppingCart,
			setCurrentPrebasketProduct,
			setPreBasketUpdated,
			setShowChatWindow,
			setCartArray,
		]
	);

	const handleOnInteractiveElementEnter = useCallback(() => {
		if (!currentProduct || currentProduct.length === 0) {
			return;
		}
		if (currentProduct.custom?.interactiveWidget?.settings?.general?.onEntry?.value === 'pause') {
			(window as any).GoBrain.widgets(currentPlayerId).pause();
		}
	}, [currentProduct, currentPlayerId]);

	const handleClickOnInteractiveElement = useCallback(
		(event: React.MouseEvent, targetElement: any) => {
			const pos = typeof currentPosition === 'number' ? currentPosition : 0;
			let elementActions = targetElement.action.value;
			event.stopPropagation();
			event.preventDefault();
			handlePlayerHover?.();

			window.parent.postMessage(
				{
					origin: 'Qbrick.Interact.Widget.Element',
					identifier: 'qbrick-player-analytics-message',
					command: {
						type: 'AnalyticsEvent',
						action: 'click',
						data: 'clickedInteractiveElement',
						mediaName: mediaName,
						mediaId: mediaId,
						accountId: accountId,
						duration: duration,
						isMediaLive: isMediaLive,
						currentPosition: isMediaLive ? Date.now() : pos,
					},
				},
				'*'
			);
			const urlOpenTarget = configParams.uniqueFrame === 'true' ? '_self' : '_parent';
			const uniqueFrameParam = configParams.uniqueFrame === 'true' ? '&uniqueFrame=true' : '';

			for (const key in elementActions) {
				const elementValue = elementActions[key]?.value ?? '';
				switch (key) {
					case INTERACT_ELEMENT_ACTIONS.openUrl:
						window.open(
							`${elementValue}${elementActions[key].target === '_blank' ? '' : elementValue.includes('qplayer') ? uniqueFrameParam : ''}`,
							elementActions[key].target === '_blank' ? elementActions[key].target : urlOpenTarget
						);
						break;
					case INTERACT_ELEMENT_ACTIONS.jumpToTime:
						(window as any).GoBrain.widgets(currentPlayerId).position(elementActions[key].value);
						(window as any).GoBrain.widgets(currentPlayerId).play();
						break;
					case INTERACT_ELEMENT_ACTIONS.loadVideo: {
						setCurrentInteractiveElement([]);
						setShouldChangeCurrentMedia(true);
						(window as any).GoBrain.widgets(currentPlayerId).data(elementActions[key].value, {}, true);
						break;
					}
					case INTERACT_ELEMENT_ACTIONS.videoToPlaylist:
						(window as any).GoBrain.widgets(currentPlayerId).data(elementActions[key].value, {}, false);
						break;
					case INTERACT_ELEMENT_ACTIONS.playPause:
						if (elementActions[key].value === INTERACT_ELEMENT_ACTIONS.play) {
							(window as any).GoBrain.widgets(currentPlayerId).play();
						} else if (elementActions[key].value === INTERACT_ELEMENT_ACTIONS.pause) {
							(window as any).GoBrain.widgets(currentPlayerId).pause();
						}
						break;
					case INTERACT_ELEMENT_ACTIONS.customEvent:
						break;
					case INTERACT_ELEMENT_ACTIONS.fadeOut:
						if (elementActions[key].value === true) {
							(event.currentTarget as HTMLElement).classList.add(INTERACT_ELEMENT_ACTIONS.fadeOut);
						}
						break;
				}
			}
		},
		[
			currentPosition,
			handlePlayerHover,
			mediaName,
			mediaId,
			accountId,
			duration,
			isMediaLive,
			currentPlayerId,
			setCurrentInteractiveElement,
			setShouldChangeCurrentMedia,
			configParams
		]
	);

	const handleClickOnWidget = useCallback(
		(e: React.MouseEvent, element: any) => {
			e.stopPropagation();

			handlePlayerHover?.();

			const {
				data: { events },
			} = element;
			const event = (events ?? []).find((event: any) => event.type === WIDGET_ACTIONS.clickActions.type);

			if (event) {
				const { actions } = event;

				(actions ?? []).filter(Boolean).forEach((action: any) => {
					const { type, metadata } = action;

					setChangeSceneType(type);
					const urlOpenTarget = configParams.uniqueFrame === 'true' ? '_self' : '_parent';
					const uniqueFrameParam = metadata.openNewTab ? '' : configParams.uniqueFrame === 'true' ? '&uniqueFrame=true' : '';

					switch (type) {
						case WIDGET_ACTIONS.clickActions.goToScene: {
							if (sceneConfig?.currentScene) {
								updateSceneHistory(sceneConfig?.currentScene);
							}

							const currentSceneId = sceneConfig?.currentScene;
							const currentSceneData = currentSceneId && (scenario?.scenes ?? []).find((s: any) => s.id === currentSceneId);

							let transitionOut = PLAYER_TRANSITIONS_OUT.fadeOut;
							if (currentSceneData?.metadata?.transition) {
								transitionOut = currentSceneData.metadata.transition.out ?? PLAYER_TRANSITIONS_OUT.fadeOut;
							}

							// iOS: call play() on the incoming scene's player synchronously within
							// the gesture handler. That player is initialized at volume=0
							// (non-visible players start muted), so iOS allows the play()
							// call. volume(currentVolumeLevel) is set later by onPlayerQueueItem.
							if (isIOS) {
								const incomingMedia = mediasData?.find((m: any) => m.sceneId === metadata.sceneId);
								const incomingPlayerId = `player-${incomingMedia?.id}-${configParams.containerKey}`;
								const incomingWidget = (window as any).GoBrain.widgets(incomingPlayerId);
								incomingWidget?.play?.();
							}
							setTransitionLayer(true);
							setTransitionOut(transitionOut);
							setPlayerCurrentPosition(0);
							// Two rAFs guarantee the browser has completed at least one paint
							// with the transition layer visible before the scene switches.
							requestAnimationFrame(() => {
								requestAnimationFrame(() => {
									setSceneConfig?.({ currentScene: metadata.sceneId });
									setCurrentProduct([]);
									setExitingElements([]);
									setGroupExit(null);
									setPlayerCurrentPosition(0);
								});
							});
							break;
						}
						case WIDGET_ACTIONS.clickActions.goToUrl: {
							let openUrl = `${metadata.url}${metadata.url?.includes('qplayer') ? uniqueFrameParam : ''}`;
							const isValid = openUrl.match('^(http|https)://');
							if (isValid?.[0] === 'http://' || isValid?.[0] === 'https://') {
								window.open(openUrl, metadata.openNewTab ? '_blank' : urlOpenTarget);
							} else {
								window.open(`https://${openUrl}`, metadata.openNewTab ? '_blank' : urlOpenTarget);
							}
							break;
						}
						case WIDGET_ACTIONS.clickActions.jumpToTime: {
							const { time } = metadata;
							(window as any).GoBrain.widgets(currentPlayerId)?.position?.(getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(time)));
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						}
						case WIDGET_ACTIONS.clickActions.pause: {
							(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
							break;
						}
						case WIDGET_ACTIONS.clickActions.play: {
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						}
				}});
			}
		},
		[
			handlePlayerHover,
			setChangeSceneType,
			sceneConfig,
			updateSceneHistory,
			scenario,
			setTransitionLayer,
			setTransitionOut,
			setSceneConfig,
			setPlayerCurrentPosition,
			currentPlayerId,
			configParams
		]
	);

	// The click leaves the scene, so every widget should exit with the clicked one.
	const isLeavingSceneOnClick = useCallback((element: any) => {
		const clickEvent = (element?.data?.events ?? []).find((event: any) => event.type === WIDGET_ACTIONS.clickActions.type);
		return (clickEvent?.actions ?? []).filter(Boolean).some(
			({ type, metadata }: any) =>
				type === WIDGET_ACTIONS.clickActions.goToScene || (type === WIDGET_ACTIONS.clickActions.goToUrl && !metadata?.openNewTab)
		);
	}, []);

	const handleOnWidgetEntry = useCallback(
		(element: any) => {
			const {
				data: { events },
			} = element;
			const entryEvent = (events ?? []).find((event: any) => event.type === WIDGET_ACTIONS.entryActions.type);
			const entryActions = (entryEvent?.actions ?? [])?.filter((action: any) => action.type);

			if (entryActions && entryActions.length) {
				for (const actionIndex in entryActions) {
					switch (entryActions[actionIndex]?.type) {
						case WIDGET_ACTIONS.entryActions.pause:
							(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
							break;
						case WIDGET_ACTIONS.entryActions.play:
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						case WIDGET_ACTIONS.entryActions.jumpToTime: {
							const { time } = entryActions[actionIndex].metadata;
							(window as any).GoBrain.widgets(currentPlayerId)?.position?.(getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(time)));
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						}
					}
				}
			}
		},
		[currentPlayerId]
	);

	const handleOnWidgetExit = useCallback(
		(element: any) => {
			const {
				data: { events },
			} = element;
			const exitEvent = (events ?? []).find((event: any) => event.type === WIDGET_ACTIONS.exitActions.type);
			const exitActions = (exitEvent?.actions ?? [])?.filter((action: any) => action.type);

			if (exitActions && exitActions.length) {
				for (const actionIndex in exitActions) {
					switch (exitActions[actionIndex]?.type) {
						case WIDGET_ACTIONS.exitActions.pause:
							(window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
							break;
						case WIDGET_ACTIONS.exitActions.play:
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						case WIDGET_ACTIONS.exitActions.jumpToTime: {
							const { time } = exitActions[actionIndex].metadata;
							(window as any).GoBrain.widgets(currentPlayerId)?.position?.(getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(time)));
							(window as any).GoBrain.widgets(currentPlayerId)?.play?.();
							break;
						}
					}
				}
			}
		},
		[currentPlayerId]
	);

	const shouldShowVerticalViewBox = useCallback(() => {
		if (configParams?.fromQplayerWidget === 'true') {
			return true;
		}
		const currentMedia = mediasData.find(({ id, originalId }: any) => id === mediaId || originalId === mediaId);
		return Math.floor((currentMedia?.videoWidth / currentMedia?.videoHeight) * 100) / 100 === Math.floor((9 / 16) * 100) / 100;
	}, [configParams, mediasData, mediaId]);

	// Displayed width / height of the current video, so the overlay box can match the video exactly
	const videoRatio = useMemo(() => {
		if (configParams?.fromQplayerWidget === 'true') {
			return undefined;
		}
		const currentMedia = mediasData.find(({ id, originalId }: any) => id === mediaId || originalId === mediaId);
		return currentMedia?.videoWidth && currentMedia?.videoHeight ? currentMedia.videoWidth / currentMedia.videoHeight : undefined;
	}, [configParams, mediasData, mediaId]);

	const jumpToSkipLink = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
		event.preventDefault();
		(document.getElementsByClassName(`qplayer--skip-link`)?.[0] as HTMLElement)?.focus();
	}, []);

	const handleOnInteractOverlayKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
		if (event.key !== "Tab") {
			return;
		}
		event.preventDefault();
		prevFocusElem.current = event.target as HTMLElement;
	}, []);

	const handleOnInteractOverlayKeyUp = useCallback((event: React.KeyboardEvent<HTMLElement>, interactiveElements?: any[]) => {
		if (event.key !== "Tab") {
			return;
		}

		const interactiveElementsWithId = (interactiveElements ?? [])
			.filter(Boolean)
			.map((el, index) => {
				const isImage = el.imageUrl !== undefined && (el.prodId !== undefined || el.prodId === undefined);
				return {
					id: isImage ? `elem-wrapper-image-${index}` : `btnGrpbutton-${index}`,
					y:  el.y.value,
				};
			});

		const widgetsWithId = (interactiveElements ?? [])?.length > 0 ? [] : (currentProduct ?? [])
			.map((widget: any) => {
				const { y } = widget.data?.style?.coordinate ?? {};
				return {
					id: widget.id,
					y: y,
				};
			});
		const sortedElements = _.sortBy(interactiveElementsWithId.length > 0 ? interactiveElementsWithId : widgetsWithId, "y").map((el) => el.id);
		const jumpToElement = (event: React.KeyboardEvent<HTMLElement>, elemId: string) => {
			event.preventDefault();
			document.getElementById(`${elemId}`)?.focus();
		}

		const currentSelectedElement = prevFocusElem.current;
		if (!currentSelectedElement || currentSelectedElement?.classList.contains('mainSVG')) {
			if (event.shiftKey) {
				jumpToSkipLink(event);
			} else {
				jumpToElement(event, sortedElements[0]);
			}
			return;
		}

		const currentSelectedElementId = currentSelectedElement.getAttribute('id');
		const currentSelectedElementIndex = sortedElements.indexOf(currentSelectedElementId);
		if (currentSelectedElementIndex === sortedElements.length - 1 && !event.shiftKey) {
			jumpToSkipLink(event);
		} else if (currentSelectedElementIndex === 0 && event.shiftKey) {
			jumpToSkipLink(event);
	    } else {
			const nextFocusableIndex = event.shiftKey ? currentSelectedElementIndex - 1 : currentSelectedElementIndex + 1;
			jumpToElement(event, sortedElements[nextFocusableIndex]);
		}
	}, [currentProduct, jumpToSkipLink]);

	const renderProducts = useCallback(() => {
		const pos = typeof currentPosition === 'number' ? currentPosition : 0;
		const productElement = currentProduct;
		var insertedElements: any[] = [];
		if (productElement && productElement.custom && productElement.custom.interactiveWidget && productElement.custom.interactiveWidget.settings) {
			for (const el in productElement.custom.interactiveWidget.settings) {
				if (
					Object.prototype.hasOwnProperty.call(productElement.custom.interactiveWidget.settings[el], 'type') &&
					productElement.custom.interactiveWidget.settings[el].type === 'button'
				) {
					insertedElements.push(productElement.custom.interactiveWidget.settings[el]);
				}
			}
		}

		if (
			productElement &&
			productElement.custom &&
			productElement.custom.interactiveWidget &&
			productElement.custom.interactiveWidget.settings &&
			productElement.custom.interactiveWidget.settings.general &&
			productElement.custom.interactiveWidget.settings.general.jumpToTime &&
			productElement.custom.interactiveWidget.settings.general.jumpToTime.value
		) {
			(window as any).GoBrain.widgets(currentPlayerId).position(productElement.custom.interactiveWidget.settings.general.jumpToTime.value);
			return <div onTouchEnd={handlePlayerHover} onTouchMove={onTouchMove}></div>;
		}

		if (!analyticsSent) {
			var prodIdArray: any[] = [];
			var typeOfMessage = '';

			for (var n = 0; n < insertedElements.length; n++) {
				if (insertedElements[n].imageUrl !== undefined && insertedElements[n].prodId !== undefined) {
					prodIdArray.push(insertedElements[n].prodId.value);
					typeOfMessage = 'product';
				} else if (insertedElements[n].imageUrl !== undefined && insertedElements[n].prodId === undefined) {
					typeOfMessage = 'image';
				} else {
					typeOfMessage = 'button';
				}
			}
			if (typeOfMessage === 'product') {
				window.parent.postMessage(
					{
						origin: 'Qbrick.Interact.Widget.Element',
						identifier: 'qbrick-player-analytics-message',
						command: {
							type: 'AnalyticsEvent',
							action: 'display',
							data: 'showProductElement',
							mediaName: mediaName,
							mediaId: mediaId,
							accountId: accountId,
							duration: duration,
							isMediaLive: isMediaLive,
							currentPosition: isMediaLive ? Date.now() : pos,
							productIdArray: prodIdArray,
						},
					},
					'*'
				);
			} else if (typeOfMessage === 'image') {
				window.parent.postMessage(
					{
						origin: 'Qbrick.Interact.Widget.Element',
						identifier: 'qbrick-player-analytics-message',
						command: {
							type: 'AnalyticsEvent',
							action: 'display',
							data: 'showImageElement',
							mediaName: mediaName,
							mediaId: mediaId,
							accountId: accountId,
							duration: duration,
							isMediaLive: isMediaLive,
							currentPosition: isMediaLive ? Date.now() : pos,
						},
					},
					'*'
				);
			} else if (typeOfMessage === 'button') {
				window.parent.postMessage(
					{
						origin: 'Qbrick.Interact.Widget.Element',
						identifier: 'qbrick-player-analytics-message',
						command: {
							type: 'AnalyticsEvent',
							action: 'display',
							data: 'showButtonElement',
							mediaName: mediaName,
							mediaId: mediaId,
							accountId: accountId,
							duration: duration,
							isMediaLive: isMediaLive,
							currentPosition: isMediaLive ? Date.now() : pos,
						},
					},
					'*'
				);
			}
		}
		const hasInteractiveElement = currentInteractiveElement && currentInteractiveElement.length > 0;
		return (
			<SVGWrapper
				className={isMaintainProportions === false ? 'interactOverlayContainer--adjust-proportions' : ''}
				isMobile={isMobile}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
				onTouchEnd={handlePlayerHover}
				onTouchMove={onTouchMove}
				showVerticalViewBox={shouldShowVerticalViewBox()}
				videoRatio={videoRatio}
				noMouseEvent={true}
				settings={settings}
				onKeyDown={hasInteractiveElement ? e => handleOnInteractOverlayKeyDown(e as any) : undefined}
				onKeyUp={hasInteractiveElement ? (e) => handleOnInteractOverlayKeyUp(e as any, insertedElements) : undefined}
				tabIndex={hasInteractiveElement ? 0 : undefined}
			>
				{insertedElements.map((el, i) => {
					if (el.imageUrl?.value && el.prodId !== undefined) {
						return (
							<ProductElement
								isMobile={isMobile}
								key={i}
								element={el}
								index={i}
								image
								onClick={(e: any) => handleClickOnProduct(e, el)}
								onEnter={handleOnInteractiveElementEnter}
							/>
						);
					} else if (el.imageUrl?.value && el.prodId === undefined) {
						return (
							<ProductElement
								isMobile={isMobile}
								key={i}
								element={el}
								index={i}
								onClick={(e: any) => handleClickOnInteractiveElement(e, el)}
								onEnter={handleOnInteractiveElementEnter}
							/>
						);
					} else {
						return (
							<ScalableElement
								isMobile={isMobile}
								key={i}
								element={el}
								index={i}
								onClick={(e: any) => handleClickOnInteractiveElement(e, el)}
								onEnter={handleOnInteractiveElementEnter}
							/>
						);
					}
				})}
			</SVGWrapper>
		);
	}, [currentPosition, currentProduct, analyticsSent, currentInteractiveElement, isMaintainProportions, isMobile, onMouseLeave, onMouseMove, handlePlayerHover, onTouchMove, shouldShowVerticalViewBox, videoRatio, settings, currentPlayerId, mediaName, mediaId, accountId, duration, isMediaLive, handleOnInteractOverlayKeyDown, handleOnInteractOverlayKeyUp, handleOnInteractiveElementEnter, handleClickOnProduct, handleClickOnInteractiveElement]);

	const renderSceneWidgets = useCallback(() => {
		const insertedElements = _.uniq([...(currentProduct ?? []), ...exitingElements], false, (el: any) => el.id)
			.sort((a: any, b: any) => (a.id > b.id ? 1 : -1)) // Sort by ID to keep the order of the elements
			.sort((elementA: any, elementB: any) => {
				// Sort by zIndex
				const {
					data: {
						style: { zIndex: zIndexA = 0 },
					},
				} = elementA;
				const {
					data: {
						style: { zIndex: zIndexB = 0 },
					},
				} = elementB;

				return zIndexA > zIndexB ? 1 : -1;
			});

		const hasWidgets = (currentSceneWidgets ?? []).length > 0;
		return (
			<SVGWrapper
				className={isMaintainProportions === false ? 'interactOverlayContainer--adjust-proportions' : ''}
				isMobile={isMobile}
				onMouseLeave={onMouseLeave}
				onMouseMove={onMouseMove}
				onTouchEnd={handlePlayerHover}
				onTouchMove={onTouchMove}
				showVerticalViewBox={shouldShowVerticalViewBox()}
				videoRatio={videoRatio}
				noMouseEvent={false}
				settings={settings}
				isIFrameWidgetAvailable={(insertedElements ?? []).some((el: any) => el.data?.type === WIDGET_TYPE_IFRAME)}
				onKeyDown={hasWidgets ? e => handleOnInteractOverlayKeyDown(e as any) : undefined}
				onKeyUp={hasWidgets ? (e) => handleOnInteractOverlayKeyUp(e as any) : undefined}
				tabIndex={hasWidgets ? 0 : undefined}
			>
				{insertedElements.map((el: any) => {
					const { data: { style, events } = {}, id } = el;

					if (!style) {
						return null;
					}
					const currentScene = scenario.scenes?.find(({ id }: any) => id === sceneConfig.currentScene);
					const clickEvent = events && events.find((event: any) => event.type === 'onClick' && event.actions.length > 0 && (event.actions ?? []).some((a: any) => a.type && a.type !== "none"));
					const isClickable = clickEvent ? true : false;
					// The full press/collapse ("exit") animation only makes sense when the click
					// actually navigates to another scene - other actions leave the widget on
					// screen, so they only get a brief active-state flash (see triggerClickAnimation).
					const isGoToSceneClick = clickEvent ? (clickEvent.actions ?? []).some((a: any) => a.type === WIDGET_ACTIONS.clickActions.goToScene) : false;

					return (
						<WidgetElement
							isMobile={isMobile}
							id={id}
							key={id}
							style={style}
							data={{ ...el.data, start: el.start, end: el.end }}
							clickable={isClickable}
							isGoToSceneClick={isGoToSceneClick}
							onClick={(e: any) => handleClickOnWidget(e, el)}
							onEnter={() => handleOnWidgetEntry(el)}
							onExit={() => handleOnWidgetExit(el)}
							scenarioId={`${scenario?.metadata?.title}#${scenario.id}`}
							sceneId={`${currentScene?.name}#${sceneConfig.currentScene}`}
							position={typeof currentPosition === 'number' ? currentPosition : 0}
							start={el.start}
							end={el.end}
							onExitAnimationComplete={handleWidgetExitAnimationComplete}
							groupExit={activeGroupExit}
							onClickExitStart={isLeavingSceneOnClick(el) ? (timing) => setGroupExit({ sourceId: id, ...timing }) : undefined}
						/>
					);
				})}
			</SVGWrapper>
		);
	}, [currentProduct, exitingElements, currentSceneWidgets, isMaintainProportions, isMobile, onMouseLeave, onMouseMove, handlePlayerHover, onTouchMove, shouldShowVerticalViewBox, videoRatio, settings, handleOnInteractOverlayKeyDown, handleOnInteractOverlayKeyUp, scenario?.scenes, scenario?.metadata?.title, scenario?.id, sceneConfig?.currentScene, currentPosition, handleClickOnWidget, handleOnWidgetEntry, handleOnWidgetExit, handleWidgetExitAnimationComplete, activeGroupExit, isLeavingSceneOnClick]);

	// The scene has switched (Go to scene, end of scene - with or without an exit animation - or
	// any other path): drop the old scene's widgets. Otherwise one left in currentProduct /
	// exitingElements stays mounted, renders nothing while the new scene's playhead is before its
	// start (so its exit never completes) and shows up over the new scene once it reaches its range.
	// Layout effect so the old widgets are gone before the new scene's first paint - at the switch
	// groupExit drops, which would otherwise flash them back for a frame.
	const prevSceneId = useRef(sceneConfig?.currentScene);
	useLayoutEffect(() => {
		if (prevSceneId.current !== sceneConfig?.currentScene) {
			setCurrentProduct([]);
			setExitingElements([]);
			setGroupExit(null);
		}
		prevSceneId.current = sceneConfig?.currentScene;
	}, [sceneConfig?.currentScene]);

	// ComponentDidMount equivalent
	useEffect(() => {
		getLiveMediaCurrentProduct();
	}, []);

	// ComponentDidUpdate - handle currentPosition changes
	useEffect(() => {
		const pos = typeof currentPosition === 'number' ? currentPosition : 0;
		if (currentPosition !== '' && pos > 0) {
			if (isMediaLive) {
				getLiveMediaCurrentProduct();
			} else {
				// Right after a scene switch the previous scene's player can still report its position
				// (it counts as visible until currentMediaId catches up). Matching the new scene's widgets
				// against it would show them before their start, then exit them once this scene's player
				// reports from 0.
				const isPositionFromOtherScene =
					mediaType === 'scenario' &&
					Boolean(currentPositionMediaId) &&
					!(mediasData ?? []).some((m: any) => m.id === currentPositionMediaId && m.sceneId === sceneConfig?.currentScene);
				if (isPositionFromOtherScene) {
					return;
				}
				// the scene is being left (see leavingSceneRef): its widgets are already gone
				if (mediaType === 'scenario' && isTransitionLayerEnable && leavingSceneRef.current === sceneConfig?.currentScene) {
					return;
				}

				const elements = mediaType === 'scenario' ? currentSceneWidgets : currentInteractiveElement;

				let found =
					mediaType === 'scenario'
						? (elements ?? []).filter((el: any) => (pos >= el.start && pos < el.end) || el.data.type === WIDGET_TYPE_IFRAME)
						: (elements ?? []).find((el: any) => pos >= el.start && pos < el.end);

				if (found) {
					if (mediaType !== 'scenario' || !isScenarioInteractElementsEqual(found, currentProduct)) {
						updateCurrentProduct(found);
					}
				} else if (currentProduct && currentProduct.length !== 0) {
					updateCurrentProduct([]);
				}
			}
		}
	}, [currentPosition, currentPositionMediaId, mediasData, sceneConfig?.currentScene, isTransitionLayerEnable, isMediaLive, mediaType, currentSceneWidgets, currentInteractiveElement, getLiveMediaCurrentProduct, isScenarioInteractElementsEqual, currentProduct, updateCurrentProduct]);

	// ComponentDidUpdate - handle showBackgroundElements changes
	useEffect(() => {
		let interactiveElements = document.querySelector('.elementWrapper') === null ? [] : document.querySelector('.elementWrapper')!.children;
		let shoppingBasketElement = document.querySelector('.gobrain-shopping-basket') as HTMLElement;
		let shoppingPreBasketElement = document.querySelector('.gobrain-shopping-pre-basket') as HTMLElement;

		for (var i = 0; i < interactiveElements.length; i++) {
			let element = interactiveElements[i] as HTMLElement;
			if (isElementOverlapping(element, shoppingBasketElement) || isElementOverlapping(element, shoppingPreBasketElement)) {
				element.classList.add('hideElement');
			} else {
				element.classList.remove('hideElement');
			}
		}
	}, [showBackgroundElements]);

	// ComponentDidUpdate - handle analyticsSent reset
	useEffect(() => {
		const _thisElements = mediaType === 'scenario' ? currentSceneWidgets : currentInteractiveElement;
		if (analyticsSent !== false) {
			if (!_thisElements || _thisElements.length === 0) {
				setAnalyticsSent(false);
			}
		}
	}, [mediaType, currentSceneWidgets, currentInteractiveElement, analyticsSent, setAnalyticsSent]);

	if (showCountdownModule) {
		return null;
	}

	if (mediaType === 'scenario') {
		return renderSceneWidgets();
	}
	return renderProducts();
};

export default InteractOverlay;
